import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import EventFinished from "src/components/EventFinished";
import TitleSection from "src/components/TitleSection/TitleSection";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const TierMatch2026 = () => {
  const { t, i18n } = useTranslation();
  const responsiveBgImage = useResponsiveImageUrl(
    "promotions/tier-match/tier-match-hero-image-2400x800px-v2.jpg",
  );

  return (
    <TierMatch2026Div>
      <EventFinished scheduleKeyName="tierMatch2026" eventType="promotion" />
      <Hero
        bgImage="promotions/tier-match/tier-match-hero-image-2400x800px-v2.jpg"
        image={`promotions/tier-match/tier-match-logo-2400x2400px-${i18n.language}.png`}
        buttonSettings={null}
        darkenBg={0.6}
        title=""
        descriptionBgColor="transparent"
      />
      <MainContentDiv>
        <container-div>
          <TitleSection
            as={"h1"}
            title={t("tierMatch2026.hero.title")}
            text={t("tierMatch2026.content.description")}
            borderColor="#E70A02"
          />
        </container-div>
      </MainContentDiv>

      <HowItWorksSection>
        <container-div>
          <SectionTitle center>
            {t("tierMatch2026.content.howItWorksTitle")}
          </SectionTitle>

          <Row className="g-4">
            <Col xs={12} md={4}>
              <StepCard bgImage={responsiveBgImage}>
                <CircleImageWrapper>
                  <ResponsiveImage
                    src="promotions/tier-match/VIP.png"
                    alt="Visit Host Room"
                  />
                </CircleImageWrapper>
                <StepText>
                  <ReactMarkdown>
                    {t("tierMatch2026.content.step1")}
                  </ReactMarkdown>
                </StepText>
              </StepCard>
            </Col>
            <Col xs={12} md={4}>
              <StepCard bgImage={responsiveBgImage}>
                <CircleImageWrapper>
                  <ResponsiveImage
                    src="promotions/tier-match/other-cards.png"
                    alt="Present loyalty card"
                  />
                </CircleImageWrapper>
                <StepText>
                  <ReactMarkdown>
                    {t("tierMatch2026.content.step2")}
                  </ReactMarkdown>
                </StepText>
              </StepCard>
            </Col>
            <Col xs={12} md={4}>
              <StepCard bgImage={responsiveBgImage}>
                <CircleImageWrapper>
                  <ResponsiveImage
                    src="promotions/tier-match/pg-cards.png"
                    alt="Receive upgrade"
                  />
                </CircleImageWrapper>
                <StepText>
                  <ReactMarkdown>
                    {t("tierMatch2026.content.step3")}
                  </ReactMarkdown>
                </StepText>
              </StepCard>
            </Col>
          </Row>
        </container-div>
      </HowItWorksSection>

      <ExtendSection bgImage={responsiveBgImage}>
        <ExtendContent>
          <Row className="g-0">
            <Col
              xs={12}
              md={6}
              className="order-2 order-md-1 align-self-center"
            >
              <ExtendTextWrapper>
                <SubTitleSection
                  subTitle={t("tierMatch2026.content.extendSectionTitle")}
                  text={t("tierMatch2026.content.extendSectionDescription")}
                  dark={true}
                  borderColor="#E70A02"
                />
              </ExtendTextWrapper>
            </Col>
            <Col xs={12} md={6} className="order-1 order-md-2 align-self-start">
              <ExtendImageWrapper>
                <ResponsiveImage
                  src="promotions/tier-match/roulette-game-v2.webp"
                  alt={t("tierMatch2026.content.extendSectionTitle")}
                />
              </ExtendImageWrapper>
            </Col>
          </Row>
        </ExtendContent>
      </ExtendSection>

      <ReadySection>
        <container-div>
          <SubTitleSection
            subTitle={t("tierMatch2026.content.readyTitle")}
            text={t("tierMatch2026.content.readyDescription")}
            dark={false}
          />
        </container-div>
      </ReadySection>
    </TierMatch2026Div>
  );
};

const TierMatch2026Div = styled.div`
  width: 100%;
  height: auto;
  background: var(--lightblack);
  color: var(--white);
  overflow-x: hidden;
`;

const MainContentDiv = styled.div`
  background: var(--lightblack);
  h2:after {
    border-color: #e70a02;
  }

  container-div {
    background-color: var(--lightblack);
  }
`;

const HowItWorksSection = styled.div`
  background: var(--black);
  padding: 2rem 0 4rem 0;

  @media (max-width: 768px) {
    padding: 1.5rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-weight: 700;
  font-size: 2.5rem;
  color: var(--white);
  margin-bottom: 3rem;
  text-align: ${(props) =>
    props.center ? "center" : props.left ? "left" : "center"};

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const StepCard = styled.div`
  position: relative;
  border-radius: 12px;
  padding: 2rem 4rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${(props) => props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.5;
    z-index: 0;
  }

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const CircleImageWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  margin-bottom: 0.5rem;
  border: 3px solid var(--white);
  z-index: 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StepText = styled.div`
  color: var(--white);
  font-size: 1.1rem;
  line-height: 1.5;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  p {
    margin: 0;
  }

  strong {
    color: var(--white);
    font-weight: 700;
  }
`;

const ExtendSection = styled.div`
  position: relative;
  padding: 0;
  border-top: 2px solid #ebebeb;
  border-bottom: 2px solid #ebebeb;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${(props) => props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.2;
    z-index: 0;
  }
`;

const ExtendContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
  padding: 0rem 4rem;
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ExtendTextWrapper = styled.div`
  color: var(--white);
  .subtitle-section {
    padding: 0;
  }
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const ExtendImageWrapper = styled.div`
  position: relative;
  top: -1rem;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ReadySection = styled.div`
  border-top: 4px solid var(--black);
  background: #ebebeb;
  color: var(--black);
  padding: 1rem 0;
  text-align: center;

  .subtitle-section {
    display: flex;
    flex-direction: column;
    align-items: center;

    h2 {
      color: var(--black);
      text-align: center;
      margin-bottom: 1rem;

      &:before,
      &:after {
        display: none;
      }
    }

    p {
      margin: 0 auto;
      font-size: 1.1rem;
      line-height: 1.6;
      text-align: center;

      em {
        font-size: 0.85rem;
        opacity: 0.7;
        display: block;
      }
    }
  }
`;

export default TierMatch2026;
